// The Messages console's CENTER PANE header — client identity + the tab bar.
//
// WHY TABS AND NOT A DRAWER
// The old shape put the conversation in the centre and everything else behind
// "Open full client record", which swaps the whole screen and loses your place.
// A locator mid-conversation needs the shortlist, the timeline and the money
// WITHOUT leaving the thread. Tabs keep one client on one surface.
//
// Deliberately NOT five peer tabs of equal weight — that was LANE-3's finding
// against the old drawer ("five peer tabs obscure the action spine"). The
// identity row above carries the spine: who, what stage, how long stalled.
// The tabs are just where the detail lives.
//
// Presentational only. The parent owns which tab is active and what renders.

import React from "react";
import { Box, Stack, Tooltip, Typography } from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { statusConfig } from "../clients/config";
import type { ClientStatus } from "../clients/types";

export type WorkspaceTabId =
  | "conversation"
  | "shortlist"
  | "timeline"
  | "money"
  | "notes";

export interface WorkspaceTab {
  id: WorkspaceTabId;
  label: string;
  /** Optional count badge. Zero and null both render nothing. */
  count?: number | null;
}

export interface WorkspaceTabsProps {
  clientName: string;
  status?: string | null;
  /** Days in the current stage. Renders the stalled badge past the threshold. */
  daysInStage?: number | null;
  tabs: WorkspaceTab[];
  active: WorkspaceTabId;
  onChange: (t: WorkspaceTabId) => void;
  /** Right-hand escape hatch to the full record. Kept, demoted. */
  onOpenFullRecord?: () => void;
}

/** Past this many days in one stage, the header says so out loud. */
const STALLED_DAYS = 7;

export default function WorkspaceTabs({
  clientName,
  status,
  daysInStage,
  tabs,
  active,
  onChange,
  onOpenFullRecord,
}: WorkspaceTabsProps) {
  const cfg = statusConfig[(status || "") as ClientStatus];
  const stalled = typeof daysInStage === "number" && daysInStage >= STALLED_DAYS;

  return (
    <Box
      data-testid="workspace-tabs"
      sx={{
        flexShrink: 0,
        px: 2,
        pt: 1.5,
        borderBottom: (t) => `1px solid ${t.palette.divider}`,
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1.25} sx={{ mb: 1.25 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, letterSpacing: "-0.01em" }}>
          {clientName}
        </Typography>

        {cfg && (
          <Box
            component="span"
            data-testid="workspace-stage"
            sx={{
              fontSize: 11.5,
              fontWeight: 600,
              px: 1,
              py: 0.25,
              borderRadius: 1,
              color: cfg.color,
              bgcolor: `${cfg.color}22`,
              whiteSpace: "nowrap",
            }}
          >
            {cfg.label}
          </Box>
        )}

        {stalled && (
          <Tooltip title={`No stage change in ${daysInStage} days`}>
            <Stack
              direction="row"
              alignItems="center"
              spacing={0.5}
              data-testid="workspace-stalled"
              sx={{
                px: 1,
                py: 0.25,
                borderRadius: 1,
                bgcolor: (t) => `${t.palette.error.main}1F`,
                color: "error.main",
              }}
            >
              <WarningAmberIcon sx={{ fontSize: 13 }} />
              <Box component="span" sx={{ fontSize: 11.5, fontWeight: 600 }}>
                Stalled {daysInStage}d
              </Box>
            </Stack>
          </Tooltip>
        )}

        <Box sx={{ flex: 1 }} />

        {onOpenFullRecord && (
          <Box
            component="button"
            onClick={onOpenFullRecord}
            data-testid="workspace-open-full"
            sx={{
              border: (t) => `1px solid ${t.palette.divider}`,
              bgcolor: "transparent",
              cursor: "pointer",
              fontFamily: "inherit",
              fontSize: 12.5,
              fontWeight: 600,
              color: "text.secondary",
              height: 30,
              px: 1.25,
              borderRadius: 1.25,
              "&:hover": { color: "text.primary", bgcolor: "action.hover" },
            }}
          >
            Full record
          </Box>
        )}
      </Stack>

      <Stack direction="row" spacing={0.25}>
        {tabs.map((t) => {
          const on = t.id === active;
          return (
            <Box
              key={t.id}
              component="button"
              onClick={() => onChange(t.id)}
              data-testid={`workspace-tab-${t.id}`}
              aria-selected={on}
              role="tab"
              sx={{
                border: "none",
                background: "none",
                cursor: "pointer",
                fontFamily: "inherit",
                fontSize: 13.5,
                fontWeight: on ? 700 : 500,
                color: on ? "text.primary" : "text.secondary",
                px: 1.5,
                pt: 1,
                pb: 1.25,
                display: "flex",
                alignItems: "center",
                gap: 0.75,
                borderBottom: (th) =>
                  `2px solid ${on ? th.palette.primary.main : "transparent"}`,
                transition: "color 120ms ease",
                "&:hover": on ? {} : { color: "text.primary" },
              }}
            >
              {t.label}
              {typeof t.count === "number" && t.count > 0 && (
                <Box
                  component="span"
                  sx={{
                    fontSize: 10.5,
                    fontWeight: 700,
                    minWidth: 18,
                    px: 0.5,
                    borderRadius: 0.75,
                    bgcolor: "action.selected",
                    color: "text.secondary",
                    textAlign: "center",
                  }}
                >
                  {t.count}
                </Box>
              )}
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
}
